/**
 * Portfolio Map guide cells — ported from my-accounts-portfolio-v4.html
 * PM_GUIDES (~7099-7179). Shared by scatter + matrix lenses.
 */

export type GuideCell = {
  name: string;
  desc: string;
  color: string;
  action: string;
  next?: string;
};

/** Colors for ahbh3x3 / clusteral cells — match PortfolioMapTab CELL_META. */
const AHBH_GUIDE: GuideCell[] = [
  {
    name: "Air cover / resilient",
    desc: "High AH despite BH concern — strong execution under external pressure.",
    color: "#b7591a",
    action:
      "Use internal strength as air cover while monitoring external deterioration.",
  },
  {
    name: "Mixed conditions",
    desc: "Strong KPIs with stable external context.",
    color: "#64748b",
    action:
      "Protect KPI strength and watch for movement in external conditions.",
  },
  {
    name: "Best zone",
    desc: "High AH + strong BH — the strongest combined health state.",
    color: "#15704a",
    action:
      "Protect and compound; avoid creating unnecessary intervention work.",
  },
  {
    name: "Most urgent",
    desc: "Low AH + BH concern — compound internal and external risk.",
    color: "#b42318",
    action:
      "Intervene on both fronts and establish a coordinated recovery plan.",
    next: "Review account priorities first.",
  },
  {
    name: "Stabilize + intervene",
    desc: "Weak KPIs with mixed external context.",
    color: "#b7591a",
    action:
      "Stabilize KPI performance and monitor the external context for directional change.",
  },
  {
    name: "Recovery runway",
    desc: "Low AH with strong BH — external strength can support KPI recovery.",
    color: "#2563eb",
    action:
      "Use the external runway to address internal performance before conditions weaken.",
  },
  {
    name: "No KPI score · BH concern",
    desc: "External warning exists, but internal health is unverified.",
    color: "#b42318",
    action:
      "Prioritize KPI ingestion and review the external concern without assuming internal failure.",
  },
  {
    name: "No KPI score · BH stable",
    desc: "External conditions are stable, but no internal score exists.",
    color: "#64748b",
    action:
      "Add KPI evidence before classifying account health or setting intervention priority.",
  },
  {
    name: "No KPI score · BH strong",
    desc: "External context is promising, but internal health remains unknown.",
    color: "#15704a",
    action:
      "Verify internally through KPI ingestion before assuming the account is healthy.",
  },
];

/** Order matches mock grid: growth→defend→triage rows × low→mid→high cols. */
const CLUSTER_AL_GUIDE: GuideCell[] = [
  {
    name: "Monitor alignment gap",
    desc: "Commercial momentum rests on a thin strategic foundation.",
    color: "#b7591a",
    action:
      "Confirm the AL goal, identify AL-critical contacts, and set the relationship work needed to close the gap.",
  },
  {
    name: "Alignment developing",
    desc: "Health and alignment are progressing, but not yet fully reinforced.",
    color: "#15704a",
    action:
      "Protect momentum and advance the next AL milestone through the critical-path relationships.",
  },
  {
    name: "Realized partnership",
    desc: "Performance and alignment reinforce each other.",
    color: "#0f766e",
    action:
      "Protect and compound through durable relationship architecture and adjacent expansion.",
  },
  {
    name: "Drift risk",
    desc: "Stable-looking health masks a weak strategic foundation.",
    color: "#b7591a",
    action:
      "Review alignment drift, define the next AL milestone, and identify relationships that prevent decline.",
  },
  {
    name: "Defend / on track",
    desc: "Health is controlled and a partial alignment foundation exists.",
    color: "#64748b",
    action:
      "Maintain health and progress alignment through the next achievable milestone.",
  },
  {
    name: "Execution review",
    desc: "Strong alignment is not yet producing Growth outcomes.",
    color: "#2563eb",
    action:
      "Investigate commercial execution before prescribing more relationship investment.",
  },
  {
    name: "Critical — no foundation",
    desc: "Failing health and weak alignment leave little recovery access.",
    color: "#b42318",
    action:
      "Build recovery access while sequencing the immediate account-health intervention.",
  },
  {
    name: "Defend the foundation",
    desc: "Some alignment exists but may erode during recovery.",
    color: "#c2410c",
    action:
      "Protect current relationships and use them to support the recovery sequence.",
  },
  {
    name: "Alignment-led recovery",
    desc: "Established strategic alignment can become a recovery lever.",
    color: "#b7591a",
    action:
      "Activate sponsors as co-recovery partners rather than building new relationships first.",
  },
];

export type ScatterQuadrantCornerLabels = {
  topLeft: { title: string; color: string };
  topRight: { title: string; color: string };
  bottomLeft: { title: string; color: string };
  bottomRight: { title: string; color: string };
};

type ScatterQuadrantKey = keyof ScatterQuadrantCornerLabels;

/** PM_GUIDES cell index per 2×2 scatter quadrant (top → bottom, left → right). */
const SCATTER_GUIDE_QUADRANT_INDEX: Record<
  string,
  Record<ScatterQuadrantKey, number>
> = {
  bh_fit: { topLeft: 0, topRight: 1, bottomLeft: 2, bottomRight: 3 },
  net_signal_fit: { topLeft: 0, topRight: 1, bottomLeft: 2, bottomRight: 3 },
  bh_ah: { topLeft: 0, topRight: 1, bottomLeft: 2, bottomRight: 3 },
  cluster: { topLeft: 0, topRight: 1, bottomLeft: 2, bottomRight: 3 },
};

/** Same soft wash as guide cards (`${color}0d` ≈ 5% alpha). */
/** Soft tint over the plot while View Guide is open (keeps dots as faint glow). */
const GUIDE_WASH_ALPHA = 0x12 / 255;

function normalizeHexColor(hex: string): string | null {
  let normalized = hex.trim().replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map(c => c + c)
      .join("");
  }
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  return normalized.toLowerCase();
}

/** Background tint shared by guide cards and scatter quadrant fills. */
export function guideColorWash(hex: string): string {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return `rgba(226,232,240,${GUIDE_WASH_ALPHA})`;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${GUIDE_WASH_ALPHA})`;
}

/** Per-quadrant plot fills from PM_GUIDES (same hues as guide cards / corners). */
export function scatterQuadrantFillsForLens(
  lensKey: string
): Record<ScatterQuadrantKey, string> | null {
  const cells = PM_GUIDES[lensKey];
  const indexByCorner = SCATTER_GUIDE_QUADRANT_INDEX[lensKey];
  if (!cells || !indexByCorner) return null;

  const corner = (key: ScatterQuadrantKey) => {
    const cell = cells[indexByCorner[key]];
    return guideColorWash(cell?.color ?? "#64748b");
  };

  return {
    topLeft: corner("topLeft"),
    topRight: corner("topRight"),
    bottomLeft: corner("bottomLeft"),
    bottomRight: corner("bottomRight"),
  };
}

/** Corner titles + guide colors for scatter lenses. */
export function buildScatterQuadrantCornerLabels(
  lensKey: string
): ScatterQuadrantCornerLabels | undefined {
  const cells = PM_GUIDES[lensKey];
  const indexByCorner = SCATTER_GUIDE_QUADRANT_INDEX[lensKey];
  if (!cells || !indexByCorner) return undefined;

  const corner = (key: ScatterQuadrantKey) => {
    const cell = cells[indexByCorner[key]];
    return {
      title: cell.name,
      color: cell.color,
    };
  };

  return {
    topLeft: corner("topLeft"),
    topRight: corner("topRight"),
    bottomLeft: corner("bottomLeft"),
    bottomRight: corner("bottomRight"),
  };
}

export const PM_GUIDES: Record<string, GuideCell[]> = {
  bh_fit: [
    {
      name: "Steady State",
      desc: "Low Fit + high BH — healthy externally, but not strategically central.",
      color: "#d97706",
      action:
        "Maintain selectively and revisit Fit when strategy or market conditions change.",
      next: "Use Actions only when a specific signal warrants attention.",
    },
    {
      name: "Priority Growth",
      desc: "High Fit + high BH — strategic relevance and external strength reinforce each other.",
      color: "#16a34a",
      action:
        "Protect momentum and advance the AL/RL foundation without forcing a premature investment decision.",
      next: "Check attention signals for emerging risk or opportunity context.",
    },
    {
      name: "Monitor",
      desc: "Low Fit + BH concern — external weakness on a lower-priority account.",
      color: "#64748b",
      action:
        "Limit proactive effort, monitor material BH changes, and avoid creating unnecessary relationship work.",
      next: "Review only critical account actions or major signal changes.",
    },
    {
      name: "Urgent Intervention",
      desc: "High Fit + BH concern — strategically important but externally exposed.",
      color: "#dc2626",
      action:
        "Protect relationship continuity and investigate the external pressure before setting the response.",
      next: "Review attention signals and urgent account actions.",
    },
  ],
  net_signal_fit: [
    {
      name: "Selective Opportunity",
      desc: "Low Fit + opportunity-led public signal — promising context without strategic priority.",
      color: "#d97706",
      action:
        "Explore selectively and avoid escalating attention until strategic relevance or evidence strengthens.",
      next: "Return to BH × Fit when strategic context needs confirmation.",
    },
    {
      name: "Invest Aggressively",
      desc: "High Fit + opportunity-led public signal — favorable conditions around a strategic account.",
      color: "#16a34a",
      action:
        "Explore the opportunity, validate internal health, and identify where focused attention may create leverage.",
      next: "View health cohorts before committing investment.",
    },
    {
      name: "Deprioritize",
      desc: "Low Fit + risk-led public signal — weak context without strong strategic pull.",
      color: "#64748b",
      action:
        "Monitor for meaningful change and limit proactive effort unless a critical signal fires.",
      next: "Review critical Actions only.",
    },
    {
      name: "Protect Urgently",
      desc: "High Fit + risk-led public signal — strategic importance is exposed to adverse context.",
      color: "#dc2626",
      action:
        "Investigate the risk, preserve executive coverage, and establish whether protective action is needed.",
      next: "View health cohorts and review urgent Actions.",
    },
  ],
  bh_ah: [
    {
      name: "Perception Gap",
      desc: "Strong BH + low AH — external runway exists while internal execution lags.",
      color: "#d97706",
      action:
        "Diagnose the KPI gap and use favorable external conditions as recovery runway.",
      next: "Review exact account actions and owners.",
    },
    {
      name: "Healthy All Around",
      desc: "Strong BH + high AH — internal and external health reinforce each other.",
      color: "#16a34a",
      action:
        "Protect the state, compound momentum, and advance alignment goals deliberately.",
      next: "Add alignment context when AL coverage is available.",
    },
    {
      name: "Double Red Flag",
      desc: "Low BH + low AH — internal weakness and external pressure compound.",
      color: "#64748b",
      action:
        "Intervene on both fronts and establish the immediate recovery sequence.",
      next: "Review account priorities and urgent Actions.",
    },
    {
      name: "Resilient Account",
      desc: "Low BH + high AH — strong execution is holding against external headwinds.",
      color: "#dc2626",
      action:
        "Protect execution, monitor external deterioration, and preserve relationship air cover.",
      next: "Review attention signals for changing external risk.",
    },
  ],
  cluster: [
    {
      name: "Triage",
      desc: "High risk + low opportunity — stabilize before investing further.",
      color: "#dc2626",
      action:
        "Assign an owner, activate executive support, and sequence urgent recovery actions.",
      next: "Clear the highest-severity attention signals first.",
    },
    {
      name: "Defend",
      desc: "High risk + high opportunity — protect upside while reducing exposure.",
      color: "#d97706",
      action:
        "Prioritize exec coverage, resolve open escalations, and lock renewal posture.",
      next: "Preserve expansion options once risk is contained.",
    },
    {
      name: "Monitor",
      desc: "Low risk + low opportunity — healthy enough to run lightly.",
      color: "#64748b",
      action:
        "Automate health touchpoints and keep a light quarterly review cadence.",
      next: "Watch for new opportunity signals before increasing investment.",
    },
    {
      name: "Expand",
      desc: "Low risk + high opportunity — conditions favor growth investment.",
      color: "#16a34a",
      action:
        "Open expansion conversations and advance AL/RL goals while conditions are favorable.",
      next: "Nominate for reference paths once momentum is confirmed.",
    },
  ],
  ahbh3x3: AHBH_GUIDE,
  cluster_al: CLUSTER_AL_GUIDE,
};

/** Mock's PM_TRANSITIONS — "Suggested next view" above the right panel
 * walks attention signals → health cohorts → exact scores / alignment. */
export const PM_TRANSITIONS: Record<string, { lens: string; label: string }[]> =
  {
    bh_fit: [{ lens: "net_signal_fit", label: "Check attention signals →" }],
    net_signal_fit: [{ lens: "ahbh3x3", label: "View health cohorts →" }],
    bh_ah: [{ lens: "ahbh3x3", label: "View health matrix →" }],
    cluster: [{ lens: "cluster_al", label: "Add alignment context →" }],
    ahbh3x3: [
      { lens: "bh_ah", label: "View exact scores →" },
      { lens: "cluster_al", label: "Add alignment context →" },
    ],
    cluster_al: [{ lens: "ahbh3x3", label: "Compare health posture →" }],
  };

export function isMatrixGuideLens(lensKey: string): boolean {
  return lensKey === "ahbh3x3" || lensKey === "cluster_al";
}
